function getComponentKey(element) {
  return Array.from(element.classList).find((className) => (
    className.startsWith('formio-component-')
    && !['formio-component', 'formio-component-file', 'formio-component-hidden'].includes(className)
  ))?.replace('formio-component-', '') || ''
}

function normalizeToArray(value) {
  if (Array.isArray(value)) {
    return value
  }

  if (!value) {
    return []
  }

  return [value]
}

function getColumnComponents(columns) {
  const result = []

  normalizeToArray(columns).forEach((column) => {
    result.push(...normalizeToArray(column?.components))
  })

  return result
}

function getRowComponents(rows) {
  const result = []

  normalizeToArray(rows).forEach((row) => {
    normalizeToArray(row).forEach((cell) => {
      result.push(...normalizeToArray(cell?.components))
    })
  })

  return result
}

function findComponentByKey(components = [], key = '') {
  for (const component of normalizeToArray(components)) {
    if (component?.key === key) {
      return component
    }

    const nestedComponent = findComponentByKey(component?.components, key)
    if (nestedComponent) {
      return nestedComponent
    }

    const columnsComponent = findComponentByKey(getColumnComponents(component?.columns), key)

    if (columnsComponent) {
      return columnsComponent
    }

    const rowsComponent = findComponentByKey(getRowComponents(component?.rows), key)

    if (rowsComponent) {
      return rowsComponent
    }
  }

  return null
}

function getFileComponent(vm, element) {
  const key = getComponentKey(element)
  if (!key) {
    return null
  }

  return findComponentByKey(vm.formData?.components || vm.form?.components || [], key)
}

function getFileTypesText(filePattern = '') {
  if (!filePattern || filePattern === '*') {
    return ''
  }

  return filePattern
    .split(',')
    .map(type => type.trim())
    .filter(Boolean)
    .map(type => type.replace(/^\./, ''))
    .join(', ')
}

function getFileSizeValue(fileMaxSize) {
  if (!fileMaxSize && fileMaxSize !== 0) {
    return ''
  }

  if (typeof fileMaxSize === 'number') {
    if (fileMaxSize > 1024 * 1024) {
      return Math.round(fileMaxSize / (1024 * 1024))
    }

    return fileMaxSize
  }

  const match = `${fileMaxSize}`.match(/\d+(?:[.,]\d+)?/)
  return match ? match[0].replace(',', '.') : `${fileMaxSize}`
}

function getButtonsWidth(buttonsCount) {
  return `${Math.max(buttonsCount, 0) * 35}px`
}

function restoreMovedSourceControls(targetList, sourceList) {
  const oldCards = Array.from(targetList.querySelectorAll('.formio-file-item'))
  const sourceItems = getSourceItems(sourceList)

  oldCards.forEach((card, index) => {
    const sourceItem = sourceItems[index]
    const row = sourceItem?.querySelector('.row')
    const hiddenControls = card.querySelector('.formio-file-hidden-source')

    if (!row || !hiddenControls) {
      return
    }

    const columns = Array.from(row.children || [])
    const titleColumn = columns[0]
    const actionColumn = columns[columns.length - 1]
    const fileLink = hiddenControls.querySelector('[ref="fileLink"]')
    const removeLink = hiddenControls.querySelector('[ref="removeLink"]')

    if (fileLink && !row.querySelector('[ref="fileLink"]') && titleColumn) {
      titleColumn.appendChild(fileLink)
    }

    if (removeLink && !row.querySelector('[ref="removeLink"]')) {
      const removeTarget = actionColumn || row
      removeTarget.appendChild(removeLink)
    }
  })
}

function getRowColumns(row) {
  return Array.from(row?.children || [])
}

function getTitleColumn(columns, fileLink) {
  if (fileLink) {
    const fileLinkColumn = columns.find(column => column.contains(fileLink))
    if (fileLinkColumn) {
      return fileLinkColumn
    }
  }

  return columns[0] || null
}

function getTitleText(fileLink, titleColumn) {
  if (fileLink) {
    const fileLinkClone = fileLink.cloneNode(true)
    Array.from(fileLinkClone.querySelectorAll('.sr-only, .visually-hidden')).forEach(node => node.remove())

    const linkText = `${fileLinkClone.textContent || ''}`.trim()

    if (linkText) {
      return linkText
    }
  }

  return `${titleColumn?.textContent || ''}`.trim()
}

function createActionButton(documentRef, type, qa, title) {
  const button = documentRef.createElement('button')
  button.type = 'button'
  button.className = `s-tooltip s-btn s-btn-text is-icon is-rounded is-small is-only-icon formio-file-action formio-file-action--${type}`
  button.setAttribute('data-qa', qa)
  button.setAttribute('aria-label', title)
  button.title = title

  const content = documentRef.createElement('div')
  content.className = 's-btn-content'

  const icon = documentRef.createElement('span')
  icon.className = 'formio-file-action__icon'
  icon.setAttribute('aria-hidden', 'true')

  content.appendChild(icon)
  button.appendChild(content)

  return button
}

function renderDropzone(vm, fileElement, fileDrop, componentSchema) {
  const browseLink = fileDrop.querySelector('[ref="fileBrowse"]')
  if (!browseLink) {
    return
  }

  const loader = fileDrop.querySelector('[ref="fileProcessingLoader"]')
  const documentRef = fileDrop.ownerDocument
  const content = documentRef.createElement('div')
  const textWrapper = documentRef.createElement('div')
  const importButton = documentRef.createElement('button')
  const importButtonContent = documentRef.createElement('div')
  const importButtonLabel = documentRef.createElement('span')
  const typesBlock = documentRef.createElement('div')

  const multiple = componentSchema?.multiple !== false
  const fileSizeValue = getFileSizeValue(componentSchema?.fileMaxSize)
  const fileTypesText = getFileTypesText(componentSchema?.filePattern)

  fileElement.dataset.qa = 'import-docs-block'
  fileDrop.dataset.qa = 'drop-docs-block'
  fileDrop.classList.add('dropFile', 'h7-text')
  browseLink.classList.add('formio-file-original-browse')

  content.className = 'fileSelector_content'
  textWrapper.className = 'text- formio-file-drop-text'

  importButton.type = 'button'
  importButton.className = 'h7-text text-decoration-underline text-lowercase s-btn s-btn-text is-rounded is-small formio-file-import-btn'
  importButton.setAttribute('data-qa', 'import-docs-btn')
  importButton.disabled = !!vm.disabled
  importButton.addEventListener('click', (event) => {
    event.preventDefault()
    browseLink.click()
  })

  importButtonContent.className = 's-btn-content'
  importButtonLabel.className = 's-btn-label'
  importButtonLabel.textContent = vm.$t('Tenders.Import')

  importButtonContent.appendChild(importButtonLabel)
  importButton.appendChild(importButtonContent)

  textWrapper.append(documentRef.createTextNode(`${vm.$t(multiple ? 'Tenders.DropFile' : 'Tenders.DropFileSingle')} `))
  textWrapper.appendChild(importButton)
  textWrapper.append(documentRef.createTextNode(` ${vm.$t(multiple ? 'Tenders.FromPC' : 'Tenders.FromPCSingle')}`))

  if (fileSizeValue) {
    textWrapper.append(documentRef.createTextNode(` ${vm.$t('Tenders.FilesSize', { size: fileSizeValue })}`))
  }

  if (fileTypesText) {
    typesBlock.setAttribute('data-qa', 'import-docs-types')
    typesBlock.textContent = fileTypesText
  }

  content.appendChild(textWrapper)
  if (fileTypesText) {
    content.appendChild(typesBlock)
  }

  fileDrop.replaceChildren(content, browseLink, ...(loader ? [loader] : []))
}

function getSourceItems(sourceList) {
  return Array.from(sourceList.querySelectorAll('li.list-group-item'))
    .filter(item => !item.classList.contains('list-group-header'))
}

function getSourceList(fileElement) {
  return Array.from(fileElement.children || []).find(child => (
    child.tagName === 'UL' && child.classList.contains('list-group')
  )) || fileElement.querySelector('ul.list-group')
}

function buildRenderedList(vm, sourceList, targetList) {
  const documentRef = targetList.ownerDocument
  restoreMovedSourceControls(targetList, sourceList)
  const sourceItems = getSourceItems(sourceList)

  targetList.replaceChildren()
  targetList.style.display = sourceItems.length ? '' : 'none'

  sourceItems.forEach((item, index) => {
    const row = item.querySelector('.row')
    const fileLink = row?.querySelector('[ref="fileLink"]')
    const removeLink = row?.querySelector('[ref="removeLink"]')
    const columns = getRowColumns(row)
    const titleColumn = getTitleColumn(columns, fileLink)
    const textColumns = columns.filter(column => column !== titleColumn && !column.querySelector('[ref="removeLink"]'))
    const sizeText = textColumns[0]?.textContent?.trim() || ''
    const titleText = getTitleText(fileLink, titleColumn)
    const fileUrl = fileLink?.getAttribute('href') || ''

    const card = documentRef.createElement('div')
    const icon = documentRef.createElement('span')
    const info = documentRef.createElement('div')
    const title = documentRef.createElement('div')
    const row2 = documentRef.createElement('div')
    const size = documentRef.createElement('div')
    const buttons = documentRef.createElement('div')

    card.className = 'document-list formio-file-item'
    icon.className = 'formio-file-item__icon'
    info.className = 'document-list_info'
    title.className = 'document-list_info-title'
    title.setAttribute('data-qa', 'file-title')
    title.textContent = titleText
    row2.className = 'document-list_info-row-2'
    size.className = 'h8-text my-auto'
    size.setAttribute('data-qa', 'file-size')
    size.textContent = sizeText
    buttons.className = 'document-list_btns'

    row2.appendChild(size)
    info.append(title, row2)

    if (fileLink) {
      const downloadButton = createActionButton(documentRef, 'download', 'btn-download-file', vm.$t('Tenders.Download'))

      downloadButton.addEventListener('click', (event) => {
        event.preventDefault()
        if (typeof fileLink.click === 'function') {
          fileLink.click()
          return
        }

        if (fileUrl && fileUrl !== '#') {
          const downloadLink = documentRef.createElement('a')
          downloadLink.href = fileUrl
          downloadLink.download = titleText
          downloadLink.target = '_blank'
          downloadLink.rel = 'noopener'
          downloadLink.click()
        }
      })

      buttons.appendChild(downloadButton)
    }

    if (removeLink) {
      const deleteButton = createActionButton(documentRef, 'delete', 'btn-delete-file', vm.$t('Purchasing.Remove'))
      deleteButton.addEventListener('click', (event) => {
        event.preventDefault()
        removeLink.click()
      })
      buttons.appendChild(deleteButton)
    }

    card.style.setProperty('--btns', getButtonsWidth(buttons.childElementCount))

    card.append(icon, info, buttons)
    targetList.appendChild(card)

    if (index !== sourceItems.length - 1) {
      const divider = documentRef.createElement('hr')
      divider.className = 's-divider'
      divider.setAttribute('aria-orientation', 'horizontal')
      divider.setAttribute('role', 'separator')
      divider.style.setProperty('border-style', 'solid')
      divider.style.setProperty('border-color', 'var(--divider-color)')
      divider.style.setProperty('border-bottom-width', '1px')
      targetList.appendChild(divider)
    }
  })
}

function ensureCardStructure(fileElement, hasDropzone) {
  const documentRef = fileElement.ownerDocument
  let card = fileElement.querySelector('.formio-file-card')
  let actions = fileElement.querySelector('.formio-file-card__actions')
  let text = fileElement.querySelector('.formio-file-card__text')

  if (!card) {
    card = documentRef.createElement('div')
    card.className = 'formio-file-card'
    const label = Array.from(fileElement.children).find(child => child.tagName === 'LABEL')
    const insertTarget = Array.from(fileElement.children).find(child => child !== label && !child.classList.contains('formio-file-card'))

    if (insertTarget) {
      fileElement.insertBefore(card, insertTarget)
    } else {
      fileElement.appendChild(card)
    }
  }

  if (hasDropzone && !actions) {
    actions = documentRef.createElement('div')
    actions.className = 'formio-file-card__actions px-0 d-flex flex-column align-start'
    card.appendChild(actions)
  }

  if (!hasDropzone && actions) {
    actions.remove()
    actions = null
  }

  if (!text) {
    text = documentRef.createElement('div')
    text.className = 'formio-file-card__text'
    card.appendChild(text)
  }

  card.classList.toggle('formio-file-card--readonly', !hasDropzone)

  return { card, actions, text }
}

export function enhanceFormioFileMarkup(vm) {
  const root = vm.$el
  if (!root) {
    return
  }

  root.querySelectorAll('.formio-component-file').forEach((fileElement) => {
    const fileDrop = fileElement.querySelector('[ref="fileDrop"].fileSelector')
    const sourceList = getSourceList(fileElement)
    const { card, actions, text } = ensureCardStructure(fileElement, !!fileDrop)
    let renderedList = text.querySelector('.formio-file-rendered-list')

    card.setAttribute('data-qa', 'import-docs-block')

    if (fileDrop && actions) {
      actions.appendChild(fileDrop)
      renderDropzone(vm, fileElement, fileDrop, getFileComponent(vm, fileElement))
    }

    if (sourceList) {
      sourceList.classList.add('formio-file-hidden-source')
      text.appendChild(sourceList)

      if (!renderedList) {
        renderedList = fileElement.ownerDocument.createElement('div')
        renderedList.className = 'attachments-list main__light_green formio-file-rendered-list'
        renderedList.setAttribute('data-qa', 'imported-docs-list')
        text.appendChild(renderedList)
      }

      buildRenderedList(vm, sourceList, renderedList)
    }
  })
}